/**
 * Equity Dividend Report Helper Utilities
 * Provides formatting, calculation, and data manipulation functions
 */

/**
 * Format currency values with proper locale settings
 * @param {number|string} value - The value to format
 * @param {object} options - Formatting options
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (value, options = {}) => {
  const {
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
    locale = 'en-IN',
  } = options;

  if (value === null || value === undefined) {
    return '0.00';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '0.00';
  }

  return numValue.toLocaleString(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  });
};

/**
 * Format percentage values
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted percentage string
 */
export const formatPercentage = (value, decimals = 2) => {
  if (value === null || value === undefined) {
    return '0.00';
  }

  const numValue = Number(value);
  if (isNaN(numValue)) {
    return '0.00';
  }

  return numValue.toFixed(decimals);
};

/**
 * Calculate sum of values in an array
 * @param {array} values - Array of numbers
 * @returns {number} Sum of all values
 */
export const calculateSum = (values = []) => {
  if (!Array.isArray(values)) {
    return 0;
  }

  return values.reduce((sum, val) => {
    const numVal = Number(val);
    return sum + (isNaN(numVal) ? 0 : numVal);
  }, 0);
};

/**
 * Get or create default months array for Indian financial year
 * @returns {array} Array of month names from April to March
 */
export const getFinancialYearMonths = () => {
  return [
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
    'January',
    'February',
    'March',
  ];
};

/**
 * Get value from object safely with fallback
 * @param {object} obj - Object to get value from
 * @param {string} path - Dot-separated path (e.g., "user.profile.name")
 * @param {*} defaultValue - Default value if not found
 * @returns {*} Retrieved value or default
 */
export const getNestedValue = (obj, path, defaultValue = null) => {
  if (!obj || typeof obj !== 'object' || !path) {
    return defaultValue;
  }

  const keys = path.split('.');
  let value = obj;

  for (const key of keys) {
    if (value && typeof value === 'object' && key in value) {
      value = value[key];
    } else {
      return defaultValue;
    }
  }

  return value;
};

/**
 * Validate financial year format
 * @param {string} fy - Financial year in format "YYYY-YYYY"
 * @returns {boolean} True if valid format
 */
export const isValidFinancialYear = (fy) => {
  if (!fy || typeof fy !== 'string') {
    return false;
  }

  const pattern = /^\d{4}-\d{4}$/;
  return pattern.test(fy);
};

/**
 * Parse financial year string to start and end years
 * @param {string} fy - Financial year in format "YYYY-YYYY"
 * @returns {object} Object with startYear and endYear
 */
export const parseFinancialYear = (fy) => {
  if (!isValidFinancialYear(fy)) {
    return { startYear: null, endYear: null };
  }

  const [startYear, endYear] = fy.split('-').map(Number);
  return { startYear, endYear };
};

/**
 * Get human-readable financial year label
 * @param {string} fy - Financial year in format "YYYY-YYYY"
 * @returns {string} Label (e.g., "FY 2024-25")
 */
export const getFinancialYearLabel = (fy) => {
  const { startYear, endYear } = parseFinancialYear(fy);
  if (!startYear || !endYear) {
    return 'Invalid FY';
  }

  const shortEnd = String(endYear).slice(-2);
  return `FY ${startYear}-${shortEnd}`;
};

/**
 * Handle PDF download with proper file naming
 * @param {Blob} pdfBlob - PDF blob from API
 * @param {string} financialYear - Financial year
 * @param {string} prefix - File name prefix (default: 'Equity_Dividend_Report')
 */
export const downloadPDF = (pdfBlob, financialYear, prefix = 'Equity_Dividend_Report') => {
  if (!pdfBlob || !(pdfBlob instanceof Blob)) {
    throw new Error('Invalid PDF blob');
  }

  const url = window.URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${prefix}_${financialYear}.pdf`;
  
  document.body.appendChild(link);
  link.click();
  
  // Cleanup
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Convert data for table rendering
 * Ensures all cells have values and handles missing data
 * @param {array} data - Array of scrip objects
 * @param {array} months - Array of month names
 * @returns {array} Cleaned data for rendering
 */
export const normalizeTableData = (data = [], months = []) => {
  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((row) => {
    const normalized = {
      scrip: row.scrip || 'N/A',
      monthly: {},
      total: 0,
    };

    // Normalize monthly data
    months.forEach((month) => {
      const value = getNestedValue(row, `monthly.${month}`, 0);
      normalized.monthly[month] = Number(value) || 0;
    });

    // Calculate total
    normalized.total = calculateSum(Object.values(normalized.monthly));

    return normalized;
  });
};

/**
 * Calculate monthly totals across all scrips
 * @param {array} data - Normalized table data
 * @param {array} months - Array of month names
 * @returns {object} Object with monthly totals
 */
export const calculateMonthlyTotals = (data = [], months = []) => {
  const totals = {};

  months.forEach((month) => {
    totals[month] = calculateSum(data.map((row) => row.monthly[month] || 0));
  });

  return totals;
};

/**
 * Validate equity dividend report data structure
 * @param {object} data - Report data from API
 * @returns {boolean} True if data structure is valid
 */
export const isValidReportData = (data) => {
  if (!data || typeof data !== 'object') {
    return false;
  }

  return (
    Array.isArray(data.data) &&
    typeof data.financial_year === 'string' &&
    Array.isArray(data.months) &&
    data.months.length === 12
  );
};

/**
 * Generate empty state message based on error or data
 * @param {boolean} hasError - Whether there's an error
 * @param {boolean} isEmpty - Whether data is empty
 * @returns {object} Object with title and message
 */
export const getEmptyStateMessage = (hasError = false, isEmpty = false) => {
  if (hasError) {
    return {
      title: 'Unable to Load Report',
      message: 'There was an error loading the equity dividend report. Please try again.',
    };
  }

  if (isEmpty) {
    return {
      title: 'No Records Found',
      message: 'No equity dividend data available for the selected financial year.',
    };
  }

  return {
    title: 'Select Financial Year',
    message: 'Please select a financial year to view the equity dividend report.',
  };
};

export default {
  formatCurrency,
  formatPercentage,
  calculateSum,
  getFinancialYearMonths,
  getNestedValue,
  isValidFinancialYear,
  parseFinancialYear,
  getFinancialYearLabel,
  downloadPDF,
  normalizeTableData,
  calculateMonthlyTotals,
  isValidReportData,
  getEmptyStateMessage,
};
